import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import {
  ResponsiveChartContainer,
  LinePlot,
  BarPlot,
  ChartsYAxis,
  ChartsXAxis,
  ChartsTooltip,
  MarkPlot,
} from "@mui/x-charts";
import Paper from "@mui/material/Paper";
import { Box } from "@mui/material";
import WeekPicker from "../../Components/WeekPicker";
import SeriesLabel from "../../Components/SeriesLabel";
import CustomTooltip from "./CustomTooltip";
import ChartLoading from "../ChartLoading";
import SwitchButtons from "../SwitchButtons";
import Table from "./Table";
import { useLazyQuery } from "@apollo/client";
import { SOLD_STOCKS } from "@/services/productService";

import { Colors } from "@/theme";
import useStyles from "./styles";
import { MERCHANT_ID } from "@/constants/env";
import { getDataForChart, getWeekTextForLabel } from "./helper";
import useUpdateEffect from "@/hooks/useUpdateEffect";
import { getFromToVariablesForWeekPicker } from "../helper";

const CHART_HEIGHT = 400;
const PERIOD = 24 * 3600;

const ChartVolume = ({
  refetchVolume,
  store,
  product,
  main_data,
  compare_data,
  localization,
  defaultWeek,
  defaultWeekToCompare,
  onChangeStockData,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const currentWeek = useRef(moment().isoWeek());
  const currentDay = useRef(moment().isoWeekday());

  const [week, setWeek] = useState(defaultWeek);
  const [weekToCompare, setWeekToCompare] = useState(defaultWeekToCompare);
  const [hasLoaded, setHasLoaded] = useState(true);
  const weekText = getWeekTextForLabel(currentWeek.current, week);
  const weekToCompareText = getWeekTextForLabel(
    currentWeek.current,
    weekToCompare
  );

  const mainPrices = main_data.prices;
  const mainVolumes = main_data.volumes;
  const comparePrices = compare_data.prices;
  const compareVolumes = compare_data.volumes;
  const dataForChart = getDataForChart(
    mainPrices,
    mainVolumes,
    comparePrices,
    compareVolumes,
    7
  );

  const [isChart, setIsChart] = useState(true);

  const [getSoldStocks] = useLazyQuery(SOLD_STOCKS, {
    onCompleted: (data) => {
      onChangeStockData(data.main_data?.[0], data.compare_data?.[0]);
      setHasLoaded(true);
    },
    onError: () => setHasLoaded(true),
    fetchPolicy: "no-cache",
  });

  const refetchSoldStocks = () => {
    const fromToVars = getFromToVariablesForWeekPicker(week, weekToCompare);

    setHasLoaded(false);
    getSoldStocks({
      context: {
        headers: {
          merchantId: MERCHANT_ID,
          storeId: store.id,
        },
      },
      variables: {
        product_ids: [product.id],
        period: PERIOD,
        from: fromToVars.from,
        to: fromToVars.to,
        fromCompare: fromToVars.fromCompare,
        toCompare: fromToVars.toCompare,
      },
    });
  };

  useUpdateEffect(() => {
    refetchSoldStocks();
  }, [week, weekToCompare, refetchVolume]);

  const config = {
    sx: {
      "& .MuiChartsAxis-root .MuiChartsAxis-line": {
        stroke: Colors.GRAY_BORDER,
      },
      "& .MuiChartsAxis-root .MuiChartsAxis-tick": {
        stroke: Colors.GRAY_BORDER,
      },
      "& .MuiLineElement-root": {
        strokeWidth: 4,
      },
    },
    xAxis: [
      {
        data: dataForChart.xData,
        scaleType: "band",
        barGapRatio: 0,
        categoryGapRatio: 0.7,
        tickLabelStyle: {
          fontSize: 16,
        },
      },
    ],
    yAxis: [
      {
        id: "yPrice",
        tickLabelStyle: {
          display: "none",
        },
        min: Math.max(0, 2 * dataForChart.minPrice - dataForChart.maxPrice),
      },
      {
        id: "yVolume",
        tickLabelStyle: {
          display: "none",
        },
        max: dataForChart.maxVolume * 2,
      },
    ],
    series: [
      {
        id: "price_this_week",
        type: "line",
        data: dataForChart.prices,
        yAxisKey: "yPrice",
        color: Colors.BLUE,
        curve: "linear",
      },
      {
        id: "price_last_week",
        type: "line",
        data: dataForChart.pricesToCompare,
        yAxisKey: "yPrice",
        color: Colors.BLUE_LIGHT,
        curve: "linear",
      },
      {
        id: "volume_this_week",
        type: "bar",
        data: dataForChart.volumes,
        yAxisKey: "yVolume",
        color: Colors.GREEN_DARK,
      },
      {
        id: "volume_last_week",
        type: "bar",
        data: dataForChart.volumesToCompare,
        yAxisKey: "yVolume",
        color: Colors.GREEN,
      },
    ],
    height: CHART_HEIGHT,
  };

  return (
    <Paper
      sx={{
        borderRadius: 4,
        position: "relative",
        boxShadow: "none",
        marginBottom: 15,
        borderWidth: 1,
        borderColor: Colors.GRAY_BORDER,
        borderStyle: "solid",
      }}
    >
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        paddingTop={5}
        paddingX={11}
      >
        <span className={classes.codeText}>{product.product_code}</span>
        <Box display={"flex"}>
          <WeekPicker
            label={t("dashboard_screen.week")}
            week={week}
            onChange={(value) => value !== weekToCompare && setWeek(value)}
          />
          <Box ml={10}>
            <WeekPicker
              label={t("dashboard_screen.compare_with")}
              week={weekToCompare}
              onChange={(value) => value !== week && setWeekToCompare(value)}
            />
          </Box>
          <Box ml={15}>
            <SwitchButtons isChart={isChart} onSwitch={setIsChart} />
          </Box>
        </Box>
      </Box>
      {isChart ? (
        hasLoaded ? (
          <>
            <ResponsiveChartContainer {...config}>
              <LinePlot />
              <ChartsXAxis disableTicks />
              <ChartsYAxis
                position="left"
                axisId="yPrice"
                disableTicks
                label={t("dashboard_screen.price")}
                labelStyle={{ fontSize: 20 }}
              />
              <ChartsYAxis
                position="right"
                axisId="yVolume"
                disableTicks
                label={t("dashboard_screen.volume")}
                labelStyle={{ fontSize: 20 }}
              />
              <ChartsTooltip
                slots={{ itemContent: CustomTooltip }}
                trigger="item"
              />
              <MarkPlot />
              <BarPlot />
            </ResponsiveChartContainer>
            <Box display={"flex"} pb={6} justifyContent={"center"}>
              <Box mx={5}>
                <SeriesLabel
                  color={Colors.BLUE}
                  label={t("dashboard_screen.week_price", { week: weekText })}
                />
              </Box>
              <Box mx={5}>
                <SeriesLabel
                  color={Colors.BLUE_LIGHT}
                  label={t("dashboard_screen.week_price", {
                    week: weekToCompareText,
                  })}
                />
              </Box>
              <Box mx={5}>
                <SeriesLabel
                  color={Colors.GREEN_DARK}
                  label={t("dashboard_screen.week_volume", { week: weekText })}
                />
              </Box>
              <Box mx={5}>
                <SeriesLabel
                  color={Colors.GREEN}
                  label={t("dashboard_screen.week_volume", {
                    week: weekToCompareText,
                  })}
                />
              </Box>
            </Box>
          </>
        ) : (
          <Box paddingX={11} paddingY={5}>
            <ChartLoading height={400} />
          </Box>
        )
      ) : (
        <Table
          dataForChart={dataForChart}
          loading={!hasLoaded}
          weekText={weekText}
          weekToCompareText={weekToCompareText}
        />
      )}
    </Paper>
  );
};

export default ChartVolume;
